import {
  createContext,
  ReactNode,
  useEffect,
  useContext,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';
import Router from 'next/router';
import { AreasActuation } from '@/components/CardActuationArea';
import Loading from '@/components/Loading';
import { simpleSignOut } from '@/hooks/useSignout';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { IUserMembership, User } from '@/types/api/users';
import { getFirstValueTrue } from '@/utils/arrayUtils';
import { generateProgressRatePercentage } from '@/utils/calculateProgressRate';
import { ROUTER_PAGES } from '@/utils/constants';
import { MyErrorResponse } from '@/utils/handlers';
import { AxiosError } from 'axios';
import { parseCookies, setCookie } from 'nookies';

interface ISignInCredentials {
  username: string;
  password: string;
}

interface ISignInOptions {
  preventRedirect: boolean;
  successCallback?: Function;
}

interface IAuthContextData {
  user: User;
  isAuthenticated: boolean;
  defaultMembership: IUserMembership | undefined;
  progressRatePercentage: number;
  setUser: Dispatch<SetStateAction<User>>;
  signIn(
    credentials: ISignInCredentials,
    options?: ISignInOptions
  ): Promise<void>;
  updateUserData: (value: Partial<User>) => void;
  userAreaActuation: AreasActuation;
  userAreaActuationIsAllowed: boolean;
}

interface IAuthProviderProps {
  children: ReactNode;
  initialStates?: Partial<
    Pick<IAuthContextData, 'user'> & { isFetchingUser: boolean }
  >;
}

const AreasActuationAllowedToAccess: AreasActuation[] = [
  AreasActuation.SUPPLIER,
];

export const AuthContext = createContext({} as IAuthContextData);

export function AuthProvider({ children, initialStates }: IAuthProviderProps) {
  const { 'obraplay.token': token } = parseCookies();
  const [user, setUser] = useState<User>(initialStates?.user || ({} as User));
  const [isFetchingUser, setIsFetchingUser] = useState(
    !!initialStates?.isFetchingUser
  );

  const isAuthenticated = !!user.username;

  const defaultMembership = user.memberships?.find(
    membership => membership.is_default && !membership.is_refused
  );

  const CANDIDATES_BASE_TO_ENTER_THE_RATE_PROGRESS = [
    user,
    user.verified_email,
    user.verified_cpf,
    user.verified_phone,
    defaultMembership?.company.has_confirmed_configuration,
    defaultMembership?.company.has_confirmed_address,
    defaultMembership?.is_validated,
  ];
  const progressRatePercentage = generateProgressRatePercentage(
    CANDIDATES_BASE_TO_ENTER_THE_RATE_PROGRESS
  );

  async function signIn(
    { username, password }: ISignInCredentials,
    options?: ISignInOptions
  ) {
    try {
      const response = await api.post('/auth/jwt/create/', {
        username,
        password,
      });

      setCookie(undefined, 'obraplay.token', response.data['access'], {
        maxAge: 60 * 60 * 24, // 1 day
        path: '/',
      });
      setCookie(undefined, 'obraplay.refresh', response.data['refresh'], {
        maxAge: 60 * 60 * 24,
        path: '/',
      });

      api.defaults.headers.common[
        'Authorization'
      ] = `Bearer ${response.data['access']}`;

      if (options?.successCallback) {
        options.successCallback();
      }

      if (options?.preventRedirect) {
        setUser(prevState => ({
          ...prevState,
          username,
        }));
        return;
      }

      setTimeout(() => {
        if (Router.query.next) {
          Router.push(Router.query.next as string).finally(() => {
            setUser(prevState => ({
              ...prevState,
              username,
            }));
          });
          return;
        }

        Router.push(ROUTER_PAGES.home).finally(() => {
          setUser(prevState => ({
            ...prevState,
            username,
          }));
        });
      }, 0);
    } catch (error) {
      const axiosError = (error as AxiosError<MyErrorResponse>).response;

      if (axiosError?.status === 400) {
        if (axiosError.data.username) {
          Toast('error', `Username: ${axiosError.data.username[0]}`);
        }
        if (axiosError.data.password) {
          Toast('error', `Senha: ${axiosError.data.password[0]}`);
        }

        return;
      }

      if (axiosError?.status === 401) {
        Toast('error', axiosError.data.detail);
        return;
      }

      Toast('error', 'Não foi possível fazer login');
    }
  }

  function updateUserData(value: Partial<User>) {
    const userUpdate = {
      ...user,
      ...value,
    } as User;
    setUser(userUpdate);
  }

  const userAreaActuation = getFirstValueTrue([
    user.is_supplier && AreasActuation.SUPPLIER,
    user.is_candidate && AreasActuation.CANDIDATE,
    user.is_service_provider && AreasActuation.SERVICE_PROVIDER,
    user.is_constructor && AreasActuation.CONSTRUCTOR,
    user.is_customer && AreasActuation.CUSTOMER,
    user.is_obraprima_customer && AreasActuation.OBRAPRIMA_CUSTOMER,
  ]) as AreasActuation;

  const userAreaActuationIsAllowed = userAreaActuation
    ? AreasActuationAllowedToAccess.includes(userAreaActuation)
    : !userAreaActuation;

  useEffect(() => {
    if (token) {
      api
        .get('/auth/users/me/')
        .then(response => {
          setUser({
            ...response.data,
          });
        })
        .catch(() => {
          simpleSignOut();
        })
        .finally(() => {
          setIsFetchingUser(false);
        });
    } else {
      setIsFetchingUser(false);
    }
  }, [token, setIsFetchingUser]);
  return (
    <AuthContext.Provider
      value={{
        signIn,
        isAuthenticated,
        user,
        setUser,
        updateUserData,
        defaultMembership,
        progressRatePercentage,
        userAreaActuation,
        userAreaActuationIsAllowed,
      }}
    >
      {isFetchingUser ? <Loading withoutOverlay /> : children}
    </AuthContext.Provider>
  );
}

export const useUser = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw Error('Must be inside a AuthProvider');
  }

  return context;
};

export type { IAuthContextData };
